import React, { useRef, useState } from 'react';
import { confirmAction } from '../../utils/confirm';
import { getErrorMessage } from '../../services/_apiBase';
import {
    addShellyDevice, updateShellyDevice, removeShellyDevice,
    fetchShellyCloudDevices,
    ShellyDevice, ShellyCloudDevice,
} from '../../services/api';
import { colors, ui } from '../../styles/shared';
import { centerNote } from './helpers';

// ── Hämta enheter från molnet ─────────────────────────────────────────────────

const CloudDiscoverPanel: React.FC<{
    registeredIds: Set<string>;
    onRegister: (id: string, code: string) => void;
}> = ({ registeredIds, onRegister }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [cloudDevices, setCloudDevices] = useState<ShellyCloudDevice[]>([]);
    const [error, setError] = useState<string | null>(null);

    const discover = async () => {
        setLoading(true); setError(null); setOpen(true);
        try {
            setCloudDevices(await fetchShellyCloudDevices());
        } catch (e: any) {
            setError(getErrorMessage(e, 'Kunde inte hämta enheter'));
        } finally {
            setLoading(false);
        }
    };

    if (!open) {
        return (
            <button style={{ ...ui.btnLightNeutral, marginBottom: 12 }} onClick={discover}>
                🔍 Hämta enheter från molnet
            </button>
        );
    }

    return (
        <div style={{ ...ui.card, border: '1px solid #e2e8f0', background: '#fafafa' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <strong style={{ fontSize: 13 }}>Enheter i ditt Shelly Cloud-konto</strong>
                <button style={ui.btnLightNeutral} onClick={() => setOpen(false)}>✕</button>
            </div>
            {loading && <div style={{ color: '#6b7280', fontSize: 13 }}>Ansluter till Shelly Cloud…</div>}
            {error && <div style={{ color: colors.danger, fontSize: 13 }}>{error}</div>}
            {!loading && !error && cloudDevices.length === 0 && (
                <div style={{ color: '#9ca3af', fontSize: 13 }}>Inga enheter i kontot.</div>
            )}
            {!loading && cloudDevices.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {cloudDevices.map(d => {
                        const already = registeredIds.has(d.id);
                        return (
                            <div key={d.id} style={{
                                background: already ? '#f0fdf4' : colors.white,
                                border: `1px solid ${already ? '#86efac' : '#e5e7eb'}`,
                                borderRadius: 8, padding: '8px 12px',
                                fontSize: 12, display: 'flex', alignItems: 'center', gap: 8,
                            }}>
                                <span style={{ color: d.online ? '#10b981' : '#9ca3af' }}>●</span>
                                <div>
                                    <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{d.id}</div>
                                    <div style={{ color: '#6b7280' }}>{d.code} · {d.gen}</div>
                                </div>
                                {already ? (
                                    <span style={{ color: '#10b981', fontSize: 11 }}>✓ Tillagd</span>
                                ) : (
                                    <button style={ui.btnLight} onClick={() => onRegister(d.id, d.code)}>+ Lägg till</button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ── Fliken Enheter ────────────────────────────────────────────────────────────

/** Färgad tid för när enheten senast hördes av. */
const formatLastSeen = (ls: string | null) => {
    if (!ls) return <span style={{ color: '#9ca3af' }}>Aldrig</span>;
    const d = new Date(ls);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    const color = diffMin < 5 ? '#10b981' : diffMin < 30 ? '#f59e0b' : '#ef4444';
    return <span style={{ color }}>{d.toLocaleString('sv-SE')}</span>;
};

interface DevicesTabProps {
    devices: ShellyDevice[];
    loading: boolean;
    /** Läser om listan efter en ändring. */
    onReload: () => Promise<void>;
    onError: (msg: string | null) => void;
}

const DevicesTab: React.FC<DevicesTabProps> = ({ devices, loading, onReload, onError }) => {
    const [showAdd, setShowAdd] = useState(false);
    const [newDeviceId, setNewDeviceId] = useState('');
    const [newName, setNewName] = useState('');
    const [saving, setSaving] = useState(false);
    const [editingName, setEditingName] = useState<string | null>(null);
    const [editNameValue, setEditNameValue] = useState('');
    const savingName = useRef(false);

    const handleAdd = async () => {
        if (!newDeviceId.trim() || !newName.trim()) {
            onError('Enhets-ID och namn måste fyllas i'); return;
        }
        setSaving(true); onError(null);
        try {
            await addShellyDevice(newDeviceId.trim(), newName.trim());
            setNewDeviceId(''); setNewName(''); setShowAdd(false);
            await onReload();
        } catch (e: any) {
            onError(getErrorMessage(e, 'Kunde inte lägga till enheten'));
        } finally {
            setSaving(false);
        }
    };

    const handleAddFromCloud = (cloudId: string, code: string) => {
        setShowAdd(true);
        setNewDeviceId(cloudId);
        setNewName(code || cloudId);
    };

    const handleRenameStart = (device: ShellyDevice) => {
        setEditingName(device.device_id);
        setEditNameValue(device.name);
    };

    const handleRenameSave = async (deviceId: string) => {
        if (savingName.current) return;
        const trimmed = editNameValue.trim();
        if (!trimmed) { setEditingName(null); return; }
        savingName.current = true;
        try {
            await updateShellyDevice(deviceId, { name: trimmed });
            setEditingName(null);
            await onReload();
        } catch (e: any) { onError(e.message || 'Kunde inte byta namn'); }
        finally { savingName.current = false; }
    };

    const handleToggleEnabled = async (device: ShellyDevice) => {
        try {
            await updateShellyDevice(device.device_id, { enabled: !device.enabled });
            await onReload();
        } catch (e: any) { onError(e.message || 'Kunde inte spara enheten'); }
    };

    const handleRemove = async (device: ShellyDevice) => {
        if (!confirmAction(`Ta bort "${device.name}"? Det går inte att ångra.`)) return;
        try {
            await removeShellyDevice(device.device_id);
            await onReload();
        } catch (e: any) { onError(e.message || 'Kunde inte ta bort enheten'); }
    };

    const registeredIds = new Set(devices.map(d => d.device_id));

    return (
        <>
            {/* Rubrik med knapp för att lägga till */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Bevakade enheter</h3>
                <button
                    style={showAdd ? ui.btnSecondary : ui.btnPrimary}
                    onClick={() => { setShowAdd(v => !v); onError(null); }}
                >
                    {showAdd ? '✕ Avbryt' : '+ Lägg till enhet'}
                </button>
            </div>

            <CloudDiscoverPanel registeredIds={registeredIds} onRegister={handleAddFromCloud} />

            {/* Formulär för att lägga till för hand */}
            {showAdd && (
                <div style={{ ...ui.card, border: '1px solid #bfdbfe', background: '#eff6ff' }}>
                    <h4 style={{ margin: '0 0 14px', fontSize: 15 }}>Lägg till enhet</h4>
                    <p style={{ ...ui.hint, margin: '0 0 12px' }}>
                        Skriv in enhets-ID:t som visas i Shelly Cloud-appen → Device → Settings → Device Information → Device Id
                    </p>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                            <label style={ui.label}>Enhets-ID i molnet</label>
                            <input
                                value={newDeviceId}
                                onChange={e => setNewDeviceId(e.target.value)}
                                placeholder="t.ex. b48a0a1cd978"
                                style={ui.input}
                            />
                        </div>
                        <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                            <label style={ui.label}>Namn</label>
                            <input
                                value={newName}
                                onChange={e => setNewName(e.target.value)}
                                placeholder="t.ex. Huvudcentral"
                                style={ui.input}
                            />
                        </div>
                        <button
                            style={{ ...ui.btnPrimary, opacity: saving ? 0.6 : 1 }}
                            onClick={handleAdd}
                            disabled={saving}
                        >
                            {saving ? 'Sparar…' : 'Lägg till'}
                        </button>
                    </div>
                </div>
            )}

            {/* Tabell med tillagda enheter */}
            <div style={ui.card}>
                {loading ? (
                    <div style={centerNote()}>Hämtar…</div>
                ) : devices.length === 0 ? (
                    <div style={centerNote('#9ca3af')}>
                        <div style={{ fontSize: 36, marginBottom: 8 }}>⚡</div>
                        Inga enheter tillagda än. Använd <strong>Hämta enheter från molnet</strong> eller <strong>+ Lägg till enhet</strong>.
                    </div>
                ) : (
                    <div className="tbl-scroll">
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={ui.th}>Enhets-ID</th>
                                    <th style={ui.th}>Namn</th>
                                    <th style={ui.th}>Läge</th>
                                    <th style={ui.th}>Senast sedd</th>
                                    <th style={ui.th}>Åtgärder</th>
                                </tr>
                            </thead>
                            <tbody>
                                {devices.map(device => (
                                    <tr key={device.device_id} style={{ opacity: device.enabled ? 1 : 0.55 }}>
                                        <td style={ui.td}>
                                            <code style={{ fontSize: 12, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>
                                                {device.device_id}
                                            </code>
                                        </td>
                                        <td style={ui.td}>
                                            {editingName === device.device_id ? (
                                                <input
                                                    style={{ ...ui.input, width: 'auto', minWidth: 120 }}
                                                    value={editNameValue}
                                                    onChange={e => setEditNameValue(e.target.value)}
                                                    onBlur={() => handleRenameSave(device.device_id)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') handleRenameSave(device.device_id);
                                                        if (e.key === 'Escape') setEditingName(null);
                                                    }}
                                                    autoFocus
                                                />
                                            ) : (
                                                <span
                                                    style={{ cursor: 'pointer', borderBottom: '1px dashed #d1d5db' }}
                                                    onClick={() => handleRenameStart(device)}
                                                    title="Klicka för att byta namn"
                                                >
                                                    {device.name}
                                                </span>
                                            )}
                                        </td>
                                        <td style={ui.td}>
                                            <button
                                                onClick={() => handleToggleEnabled(device)}
                                                style={device.enabled ? ui.btnSmall('#d1fae5', '#065f46') : ui.btnLightNeutral}
                                            >
                                                {device.enabled ? '✓ Påslagen' : '○ Avstängd'}
                                            </button>
                                        </td>
                                        <td style={ui.td}>{formatLastSeen(device.last_seen)}</td>
                                        <td style={ui.td}>
                                            <button style={ui.btnLightDanger} onClick={() => handleRemove(device)}>
                                                🗑 Ta bort
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
};

export default DevicesTab;
